/**
 * Utilidades para manejo de fechas en zona horaria de Argentina
 * Argentina está en UTC-3 (o UTC-2 durante horario de verano)
 */

/**
 * Obtiene la fecha actual en zona horaria de Argentina en formato YYYY-MM-DD
 * @returns string - Fecha en formato YYYY-MM-DD
 */
export const getArgentinaDate = (): string => {
  const now = new Date();

  // Crear una fecha en zona horaria de Argentina
  // Argentina está en UTC-3 (o UTC-2 durante horario de verano)
  // Usamos toLocaleDateString con timeZone para obtener la fecha correcta
  const argentinaDate = new Date(
    now.toLocaleString("en-US", { timeZone: "America/Argentina/Buenos_Aires" })
  );

  // Formatear como YYYY-MM-DD
  const year = argentinaDate.getFullYear();
  const month = String(argentinaDate.getMonth() + 1).padStart(2, "0");
  const day = String(argentinaDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Obtiene la fecha de mañana en zona horaria de Argentina en formato YYYY-MM-DD
 * @returns string - Fecha de mañana en formato YYYY-MM-DD
 */
export const getArgentinaTomorrowDate = (): string => {
  const now = new Date();

  // Crear una fecha en zona horaria de Argentina
  const argentinaDate = new Date(
    now.toLocaleString("en-US", { timeZone: "America/Argentina/Buenos_Aires" })
  );

  // Agregar un día
  argentinaDate.setDate(argentinaDate.getDate() + 1);

  // Formatear como YYYY-MM-DD
  const year = argentinaDate.getFullYear();
  const month = String(argentinaDate.getMonth() + 1).padStart(2, "0");
  const day = String(argentinaDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Verifica si una fecha es válida comparándola con la fecha actual de Argentina
 * @param date - Fecha a verificar en formato YYYY-MM-DD
 * @returns boolean - true si la fecha es válida (es hoy)
 */
export const isArgentinaDateValid = (date: string): boolean => {
  const today = getArgentinaDate();
  return date === today;
};

/**
 * Verifica si ya pasaron las 6pm en zona horaria de Argentina
 * @returns boolean - true si ya pasaron las 6pm, false en caso contrario
 */
export const isAfter6PMArgentina = (): boolean => {
  const now = new Date();
  const argentinaNow = new Date(
    now.toLocaleString("en-US", { timeZone: "America/Argentina/Buenos_Aires" })
  );
  return argentinaNow.getHours() >= 18;
};

/**
 * Formatea una fecha ISO a un formato legible en español (Argentina)
 * @param dateString - Fecha en formato ISO (ej: "2025-12-02T00:00:00.000Z")
 * @returns string - Fecha formateada (ej: "02/12/2025")
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  
  try {
    const date = new Date(dateString);
    
    // Verificar que la fecha es válida
    if (isNaN(date.getTime())) {
      return "-";
    }
    
    // Formatear para zona horaria de Argentina
    return date.toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "America/Argentina/Buenos_Aires",
    });
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return "-";
  }
};

/**
 * Pone en mayúscula la primera letra de un texto, dejando el resto intacto.
 * Se usa en lugar de `text-transform: capitalize` de CSS, que capitalizaría
 * TODAS las palabras ("Jueves, 6 De Agosto").
 */
export const capitalizeFirst = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Formatea una fecha (YYYY-MM-DD o ISO) como "jueves, 6 de agosto".
 *
 * IMPORTANTE: el mes va completo a propósito. Con `month: "short"` es-AR
 * devuelve "ago" para agosto, y el traductor de Chrome lo interpreta como el
 * inglés "ago" y lo reescribe como "hace N días" para los choferes que tienen
 * la traducción automática activada. "agosto" no es ambiguo.
 *
 * La fecha se parsea componente a componente (no `new Date(string)`) para
 * evitar que "2025-08-06" se interprete como medianoche UTC y se muestre el
 * día anterior en Argentina (UTC-3).
 *
 * @param dateString - Fecha en formato YYYY-MM-DD o ISO
 * @returns string - Fecha en minúscula (ej: "jueves, 6 de agosto") o "-"
 */
export const formatLongDate = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return "-";

  const [year, month, day] = dateString.split("T")[0].split("-").map(Number);
  if (!year || !month || !day) return "-";

  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
};

/**
 * Verifica si se puede editar o eliminar una disponibilidad según la restricción de tiempo.
 * Solo la disponibilidad del día actual no se puede editar/eliminar.
 * Mañana y fechas futuras siempre se pueden editar/eliminar (para mañana se muestra advertencia en UI).
 *
 * @param date - Fecha de la disponibilidad en formato YYYY-MM-DD
 * @returns boolean - true si se puede editar/eliminar, false en caso contrario
 */
export const canEditOrDeleteAvailabilityForDate = (date: string): boolean => {
  try {
    const dateOnly = date.split("T")[0];
    if (!dateOnly || !/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      console.warn("Invalid date format:", date);
      return true;
    }

    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Argentina/Buenos_Aires",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour12: false,
    });

    const parts = formatter.formatToParts(now);
    const getPart = (type: string) =>
      parts.find((p) => p.type === type)?.value || "0";

    const argentinaYear = parseInt(getPart("year"), 10);
    const argentinaMonth = parseInt(getPart("month"), 10) - 1;
    const argentinaDay = parseInt(getPart("day"), 10);

    const todayStr = `${argentinaYear}-${String(argentinaMonth + 1).padStart(2, "0")}-${String(argentinaDay).padStart(2, "0")}`;

    const todayDateUTC = new Date(todayStr + "T00:00:00Z");
    const availabilityDateUTC = new Date(dateOnly + "T00:00:00Z");
    const diffTime = availabilityDateUTC.getTime() - todayDateUTC.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // Solo la disponibilidad de HOY no se puede editar/eliminar
    if (diffDays === 0) {
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error checking time restriction for availability", {
      date,
      error: error instanceof Error ? error.message : "Unknown error",
    });
    return true;
  }
};

/**
 * Mensaje del cartel bloqueante cuando el chofer intenta darse de baja después
 * de las 18:00 del día anterior. El backend aplica la misma regla (403 para DRIVER).
 */
export const CANCEL_CUTOFF_MESSAGE =
  "No es posible darse de baja a partir de las 18:00 del día anterior. Si necesitás darte de baja, comunicate con un coordinador.";

/**
 * Indica si la baja/edición está bloqueada por horario: la disponibilidad es de
 * mañana Y son las 18:00 o más en Argentina. Después de ese horario la baja solo
 * puede gestionarla un coordinador (el backend también lo bloquea para DRIVER).
 *
 * @param date - Fecha en formato YYYY-MM-DD
 * @returns boolean - true si es mañana y ya pasaron las 18:00 (Argentina)
 */
export const isTomorrowPastCancelCutoff = (date: string): boolean => {
  try {
    const dateOnly = date.split("T")[0];
    if (!dateOnly || !/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return false;

    const now = new Date();
    // hourCycle "h23" (no hour12: false): en WebViews viejos hour12:false
    // resuelve a h24 y la medianoche se formatea como "24" (>= 18 daría
    // un falso bloqueo entre 00:00 y 00:59). Mismo criterio que el backend.
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Argentina/Buenos_Aires",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      hourCycle: "h23",
    });

    const parts = formatter.formatToParts(now);
    const getPart = (type: string) =>
      parts.find((p) => p.type === type)?.value || "0";

    const argentinaYear = parseInt(getPart("year"), 10);
    const argentinaMonth = parseInt(getPart("month"), 10) - 1;
    const argentinaDay = parseInt(getPart("day"), 10);
    const argentinaHour = parseInt(getPart("hour"), 10);

    const todayStr = `${argentinaYear}-${String(argentinaMonth + 1).padStart(2, "0")}-${String(argentinaDay).padStart(2, "0")}`;

    const todayDateUTC = new Date(todayStr + "T00:00:00Z");
    const availabilityDateUTC = new Date(dateOnly + "T00:00:00Z");
    const diffTime = availabilityDateUTC.getTime() - todayDateUTC.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays === 1 && argentinaHour >= 18;
  } catch {
    return false;
  }
};
